import type { TourDefinition } from '@/context/TourContext';

// Map routes to their corresponding tour IDs
const ROUTE_TO_TOUR_MAP: Record<string, string> = {
  '/service-scopes': 'contracts',
  '/clients-patients': 'clients-patients', 
  '/invoices': 'invoices',
  '/people': 'employees',
  '/payments': 'payments',
  '/scheduling': 'scheduling',
  '/bank': 'bank-reconciliation',
  '/bank-reconciliation': 'bank-reconciliation',
};

const TOUR_BLOCKED_ROUTES = ['/create-company', '/invitations'];

/**
 * sessionStorage flag set by /create-company right before it leaves: the first
 * page after it opens the import-or-start-empty choice instead of the tour welcome.
 */
export const COMPANY_JUST_CREATED_KEY = 'company_just_created';

const isPathMatch = (pathname: string, route: string) => {
  return pathname === route || pathname.startsWith(`${route}/`);
};

/**
 * Check if the current route should block tour surfaces and auto-launch.
 */
export const isTourBlockedRoute = (pathname: string): boolean => {
  return TOUR_BLOCKED_ROUTES.some((route) => isPathMatch(pathname, route));
};

/**
 * Get the tour ID for the current route
 */
export const getTourForCurrentRoute = (pathname: string): string | null => {
  // Check for exact matches first
  if (ROUTE_TO_TOUR_MAP[pathname]) {
    return ROUTE_TO_TOUR_MAP[pathname];
  }
  
  // Check for partial matches (for nested routes)
  for (const [route, tourId] of Object.entries(ROUTE_TO_TOUR_MAP)) {
    if (pathname.startsWith(route)) {
      return tourId;
    }
  }
  
  return null;
};

/**
 * Get tour definition for current route
 */
export const getCurrentModuleTour = async (pathname: string): Promise<TourDefinition | null> => {
  const tourId = getTourForCurrentRoute(pathname);
  if (!tourId) return null;
  
  try {
    const { getTourById } = await import('@/components/tour/tourDefinitions');
    return getTourById(tourId) || null;
  } catch (error) {
    console.error('Error loading tour definition:', error);
    return null;
  }
};

/**
 * Check if current route has an associated tour
 */
export const hasTourForCurrentRoute = (pathname: string): boolean => {
  return getTourForCurrentRoute(pathname) !== null;
};
